import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { UseFormRegisterReturn } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { Input, type InputProps } from "@/components/ui/input";
import { cleanRBIMCode, getRBIMCodeOptions, type RbimCodeQuestionKey } from "@/lib/rbimCodeMap";
import { cn } from "@/lib/utils";
import { useWorkspaceRoot } from "../../../web-adapter";

type CodedInputProps = InputProps & {
  questionKey: RbimCodeQuestionKey;
  registration: UseFormRegisterReturn;
  value?: string;
  helperClassName?: string;
};

type PopupPosition = {
  anchor: number;
  left: number;
  maxHeight: number;
  placement: "above" | "below";
  width: number;
};

const popupGap = 4;
const popupMaxHeight = 300;
const viewportPadding = 8;

export function CodedCombobox({
  className,
  helperClassName,
  questionKey,
  registration,
  value,
  ...props
}: CodedInputProps) {
  const comboboxId = useId();
  const listboxId = `${comboboxId}-listbox`;
  const workspaceRoot = useWorkspaceRoot();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listboxRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasValidated, setHasValidated] = useState(false);
  const [position, setPosition] = useState<PopupPosition | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const result = cleanRBIMCode(questionKey, value);
  const options = getRBIMCodeOptions(questionKey);
  const hasValue = Boolean(result.code);
  const query = searchQuery.trim();
  const selectedOptionIndex = options.findIndex((option) => option.code === result.code);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeSearchText(query);

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) => {
      const searchTarget = normalizeSearchText(`${option.code} ${option.label} ${option.displayValue}`);

      return option.code.toUpperCase().startsWith(query.toUpperCase()) || searchTarget.includes(normalizedQuery);
    });
  }, [options, query]);
  const activeOption = filteredOptions[activeIndex] ?? filteredOptions[0];
  const shouldShowValidation = Boolean(result.error && hasValidated);
  const isDisabled = Boolean(props.disabled);

  const updatePosition = useCallback(() => {
    const input = inputRef.current;

    if (!input) {
      return;
    }

    const rect = input.getBoundingClientRect();
    const width = Math.min(
      Math.max(rect.width, 180),
      320,
      Math.max(window.innerWidth - viewportPadding * 2, 0),
    );
    const left = Math.min(
      Math.max(rect.left, viewportPadding),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding),
    );
    const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - popupGap - viewportPadding);
    const spaceAbove = Math.max(0, rect.top - popupGap - viewportPadding);
    const placement = spaceBelow < popupMaxHeight && spaceAbove > spaceBelow ? "above" : "below";
    const availableHeight = placement === "above" ? spaceAbove : spaceBelow;

    setPosition({
      anchor: placement === "above"
        ? window.innerHeight - rect.top + popupGap
        : rect.bottom + popupGap,
      left,
      maxHeight: Math.min(popupMaxHeight, availableHeight),
      placement,
      width,
    });
  }, []);

  const writeValue = useCallback(
    (nextValue: string) => {
      if (inputRef.current) {
        inputRef.current.value = nextValue;
      }

      void registration.onChange({
        target: { name: registration.name, value: nextValue },
        type: "change",
      });
    },
    [registration],
  );

  const openPopup = useCallback(() => {
    setSearchQuery("");
    setActiveIndex(selectedOptionIndex >= 0 ? selectedOptionIndex : 0);
    setIsOpen(true);
    requestAnimationFrame(() => {
      optionRefs.current[selectedOptionIndex >= 0 ? selectedOptionIndex : 0]?.scrollIntoView({ block: "nearest" });
    });
  }, [selectedOptionIndex]);

  const closePopup = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
    setActiveIndex(0);
  }, []);

  const selectOption = useCallback(
    (option: (typeof options)[number]) => {
      writeValue(option.code);
      setHasValidated(false);
      closePopup();
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    },
    [closePopup, writeValue],
  );

  const validateCurrentValue = useCallback(() => {
    const inputValue = inputRef.current?.value ?? "";
    const cleaned = cleanRBIMCode(questionKey, inputValue);
    const shouldKeepTypedNationality =
      questionKey === "q07_nationality" && /^0*2(?:\s*[-,:]\s*|\s+).+/i.test(inputValue.trim());
    const shouldKeepTypedOtherSpecify =
      !cleaned.error &&
      Boolean(cleaned.label?.startsWith("Others")) &&
      /^[A-Z0-9]+(?:\s*[-,:]\s*|\s+).+/i.test(inputValue.trim());

    setHasValidated(Boolean(inputValue.trim()));

    if (
      !shouldKeepTypedNationality &&
      !shouldKeepTypedOtherSpecify &&
      !cleaned.error &&
      cleaned.code &&
      cleaned.code !== inputValue
    ) {
      writeValue(cleaned.code);
    }
  }, [questionKey, writeValue]);

  useLayoutEffect(() => {
    if (isOpen) {
      updatePosition();
    }
  }, [isOpen, updatePosition, filteredOptions.length]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleReposition = () => updatePosition();
    const handlePointerDown = (event: PointerEvent) => {
      const eventPath = event.composedPath();

      if (
        (inputRef.current && eventPath.includes(inputRef.current)) ||
        optionRefs.current.some((option) => option ? eventPath.includes(option) : false)
      ) {
        return;
      }

      closePopup();
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("pointerdown", handlePointerDown);

    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [closePopup, isOpen, updatePosition]);

  useEffect(() => {
    const nextIndex = query ? 0 : selectedOptionIndex >= 0 ? selectedOptionIndex : 0;
    setActiveIndex(nextIndex);

    if (nextIndex === 0) {
      listboxRef.current?.scrollTo({ top: 0 });
    }
  }, [isOpen, query, questionKey, selectedOptionIndex]);

  useEffect(() => {
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    props.onKeyDown?.(event);

    if (event.defaultPrevented) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      event.stopPropagation();
      if (!isOpen) {
        openPopup();
      }
      setActiveIndex((currentIndex) => Math.min(currentIndex + 1, Math.max(filteredOptions.length - 1, 0)));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      event.stopPropagation();
      if (!isOpen) {
        openPopup();
      }
      setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closePopup();
      return;
    }

    if (event.key === "Enter") {
      if (isOpen && activeOption) {
        event.preventDefault();
        event.stopPropagation();
        selectOption(activeOption);
        return;
      }

      validateCurrentValue();
    }
  };

  const handleIndicatorClick = () => {
    if (isDisabled) {
      return;
    }

    openPopup();
    updatePosition();
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  return (
    <div className="relative min-w-0" data-coded-combobox="true">
      <div className="relative min-w-0">
        <Input
          autoComplete="off"
          className={cn(className, "pr-7")}
          aria-activedescendant={isOpen && activeOption ? `${listboxId}-${activeIndex}` : undefined}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={isOpen}
          aria-invalid={shouldShowValidation || undefined}
          placeholder="Select or type code"
          role="combobox"
          {...props}
          name={registration.name}
          onBlur={(event) => {
            validateCurrentValue();
            closePopup();
            registration.onBlur(event);
            props.onBlur?.(event);
          }}
          onChange={(event) => {
            setIsOpen(true);
            setSearchQuery(event.currentTarget.value);
            setHasValidated(false);
            registration.onChange(event);
            props.onChange?.(event);
          }}
          onFocus={(event) => {
            openPopup();
            updatePosition();
            props.onFocus?.(event);
          }}
          onKeyDown={handleKeyDown}
          ref={(node) => {
            inputRef.current = node;
            registration.ref(node);
          }}
        />
        <button
          type="button"
          aria-label="Open coded options"
          className={cn(
            "absolute right-1 top-1/2 grid size-6 -translate-y-1/2 place-items-center rounded text-slate-500 transition hover:bg-slate-100 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-700",
            isOpen && "text-emerald-800",
            isDisabled && "pointer-events-none text-slate-300 opacity-60",
          )}
          disabled={isDisabled}
          onClick={handleIndicatorClick}
          onMouseDown={(event) => event.preventDefault()}
          tabIndex={-1}
        >
          <ChevronDown
            className={cn("size-3.5 transition-transform", isOpen && "rotate-180")}
            aria-hidden="true"
          />
        </button>
      </div>
      {isOpen && position && createPortal(
        <div
          id={listboxId}
          ref={listboxRef}
          className="z-[9999] overflow-y-auto overscroll-contain rounded-lg border border-slate-300 bg-white py-1 text-left text-[12px] text-slate-900 shadow-lg shadow-slate-900/12 ring-1 ring-slate-900/5"
          role="listbox"
          style={{
            bottom: position.placement === "above" ? position.anchor : undefined,
            left: position.left,
            maxHeight: position.maxHeight,
            position: "fixed",
            top: position.placement === "below" ? position.anchor : undefined,
            width: position.width,
          }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => {
              const isActive = index === activeIndex;
              const isSelected = result.code === option.code && !result.error;

              return (
                <button
                  aria-selected={isSelected}
                  className={cn(
                    "flex w-full items-start gap-2 px-2.5 py-1.5 text-left leading-snug outline-none transition",
                    isActive && "bg-emerald-50 text-emerald-950",
                    isSelected && "font-semibold text-emerald-900",
                    !isActive && "hover:bg-sky-50",
                  )}
                  id={`${listboxId}-${index}`}
                  key={option.code}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => selectOption(option)}
                  ref={(node) => {
                    optionRefs.current[index] = node;
                  }}
                  role="option"
                  type="button"
                >
                  <span className="shrink-0 font-mono text-[11px] font-semibold text-slate-600">{option.code}</span>
                  <span className="min-w-0 text-slate-900">- {option.label}</span>
                </button>
              );
            })
          ) : (
            <div className="px-2.5 py-2 text-[12px] text-slate-500">No matching code or option.</div>
          )}
        </div>,
        workspaceRoot ?? document.body,
      )}
      {(hasValue || shouldShowValidation) && (
        <p
          className={cn(
            "truncate px-1 pb-1 text-left text-[10px] font-semibold leading-4",
            shouldShowValidation ? "text-destructive" : "text-emerald-800",
            helperClassName,
          )}
          title={shouldShowValidation ? "Invalid code or option." : `${result.code} - ${result.label ?? ""}`}
        >
          {shouldShowValidation ? "Invalid code or option." : result.label}
        </p>
      )}
    </div>
  );
}

export const CodedInput = CodedCombobox;

function normalizeSearchText(value: string) {
  return value
    .trim()
    .toUpperCase()
    .replace(/&/g, " AND ")
    .replace(/['']/g, "")
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}